import { User } from 'interfaces';
import { http, HttpResponse } from 'msw';
import { buildUser } from '../../factories';
import { canonicalUsers } from '../../factories/seeds/entities';
import { mockOrganizations } from './organizationMock';
import { mockRoles } from './roleMock';
import { mockSites } from './siteMock';

const organizationByID = new Map(
  mockOrganizations.map((organization) => [organization.id, organization])
);
const roleByID = new Map(mockRoles.map((role) => [role.id, role]));
const sitesByOrganizationID = mockSites.reduce<
  Map<string, Array<(typeof mockSites)[number]>>
>((map, site) => {
  const existing = map.get(site.organizationID) ?? [];
  map.set(site.organizationID, [...existing, site]);
  return map;
}, new Map());

const userOrder = [
  'c9fca33e-542a-46c8-bcdd-c72aba7e2740',
  '53569941-3c6c-4c2f-acb7-c54259406f44',
  'ead48e53-8161-489b-8f85-f7457a031a44',
];

export const mockUsers: User[] = userOrder
  .map((userID) => canonicalUsers.find((user) => user.id === userID))
  .filter((user): user is (typeof canonicalUsers)[number] => Boolean(user))
  .map((user) =>
    buildUser<User>({
      ...user,
      status: 'active' as 'active' | 'inactive',
      providers: [],
      organization: organizationByID.get(user.organization.id) ?? null,
      role: roleByID.get(user.role.id) ?? null,
      providerID: null,
      sites: [sitesByOrganizationID.get(user.organization.id)?.[0] ?? null],
    })
  );

export const testUser: User = mockUsers[mockUsers.length - 1];

export const mockUserHandlers = [
  http.post('https://cognito-identity.ca-central-1.amazonaws.com/', () => {
    return HttpResponse.json({
      IdentityId: 'ca-central-1:d8cb3b4c-2497-4324-936e-0d523919d5b7',
      Logins: {},
    });
  }),

  http.get('*/resource/user/all', () => {
    return HttpResponse.json(mockUsers);
  }),
  http.get('*/resource/user/organization', ({ request }) => {
    const url = new URL(request.url).searchParams;
    const orgID = url.get('organizationID');
    const users = mockUsers.filter(
      (user) => user.organizationID === orgID.toString()
    );

    return HttpResponse.json(users);
  }),
  // Get user by cognitoID
  http.get('*/resource/user/cognitoID', ({ request }) => {
    const url = new URL(request.url).searchParams;
    const cognitoID = url.get('cognitoID');
    const user = mockUsers.find((u) => u.cognitoID === cognitoID);
    if (!user) {
      return HttpResponse.json({ message: 'Not Found' }, { status: 404 });
    }
    return HttpResponse.json(user);
  }),

  http.post('*/resource/user', async ({ request }) => {
    const user = await request.json();
    return HttpResponse.json(user);
  }),
  http.put('*/resource/user', async ({ request }) => {
    const user = await request.json();
    return HttpResponse.json(user);
  }),
  // User settings (GET + PUT)
  http.get('*/resource/user/settings', ({ request }) => {
    const url = new URL(request.url).searchParams;
    const userID = url.get('userID');
    // Return deterministic, non-default settings so tests can assert hydration changes values.
    return HttpResponse.json({
      chartType: 'line',
      mfaPreference: true,
      editPageLayout: 'unified',
      fiscalYearEnd: 3,
      providerDashboardSummaryMode: 'pay-period',
      providerDashboardDateBasis: 'billed-date',
      defaultSiteID: userID ? `${userID}-default-site` : 'default-site-x',
    });
  }),
  http.put('*/resource/user/settings', async ({ request }) => {
    // Echo back body (acts as successful persistence) and include mark of updated values.
    const body = (await request.json()) as any; // { userID, body: { ...settings } }
    return HttpResponse.json(body.body || {});
  }),
];
